'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Product } from '@/data/products';

export type CartLine = {
  slug: string;
  qty: number;
};

type CartState = {
  lines: CartLine[];
  add: (product: Product, qty?: number) => void;
  remove: (slug: string) => void;
  setQty: (slug: string, qty: number) => void;
  clear: () => void;
};

const CartContext = createContext<CartState | null>(null);

const LS_KEY = 'bondex_cart_v1';

function safeParse(value: string | null): CartLine[] {
  if (!value) return [];
  try {
    const data = JSON.parse(value) as unknown;
    if (!Array.isArray(data)) return [];
    return data
      .filter((x) => x && typeof x === 'object')
      .map((x) => x as { slug?: unknown; qty?: unknown })
      .filter((x) => typeof x.slug === 'string' && typeof x.qty === 'number')
      .map((x) => ({ slug: x.slug as string, qty: Math.max(1, Math.floor(x.qty as number)) }));
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  // localStorage mobilde bazen çalışmaz - try-catch ile koruma
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return safeParse(localStorage.getItem(LS_KEY));
      }
    } catch (e) {
      console.warn('localStorage erişilemedi:', e);
    }
    return [];
  });

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(LS_KEY, JSON.stringify(lines));
      }
    } catch (e) {
      console.warn('localStorage yazılamadı:', e);
    }
  }, [lines]);

  const value = useMemo<CartState>(() => {
    return {
      lines,
      add: (product, qty = 1) => {
        setLines((prev) => {
          const existing = prev.find((l) => l.slug === product.slug);
          if (!existing) return [...prev, { slug: product.slug, qty }];
          return prev.map((l) => (l.slug === product.slug ? { ...l, qty: l.qty + qty } : l));
        });
      },
      remove: (slug) => setLines((prev) => prev.filter((l) => l.slug !== slug)),
      setQty: (slug, qty) =>
        setLines((prev) =>
          prev
            .map((l) => (l.slug === slug ? { ...l, qty: Math.max(1, Math.floor(qty)) } : l))
            .filter((l) => l.qty > 0)
        ),
      clear: () => setLines([]),
    };
  }, [lines]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
